import React, { useEffect, useState } from 'react'; // eslint-disable-line
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { FormGroup, ControlLabel, HelpBlock, Button, Modal, FormControl } from 'react-bootstrap';
import JSONPretty from 'react-json-pretty';
import Fa from 'react-fontawesome';
import JSONPrettyMon from 'react-json-pretty/dist/monikai';
import CopyToClipboard from 'react-copy-to-clipboard';
import { reduce, indexOf, map, delay, debounce } from 'lodash';
import CRMInstanceName from '../../../../components/crm-instance-name';
import { getFeedbackAPIkey, refreshFeedbackAPIkey } from '../../SetupActions';
import ProgessBar from '../../../../components/progress-bar';
import DatetimeDisplay from '../../../../components/date-time-display';

function APIKeyInfo(props) {
  const { submitting, invalid } = props;
  const dispatch = useDispatch();
  const [apikeyHeader, setApikeyHeader] = useState({});
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState(null);
  const [logs, setLogs] = useState([]);
  const [botVersion, setBotVersion] = useState(null);
  const [botVersionLastUsed, setBotVersionLastUsed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLogs, setShowLogs] = useState(false);


  const handleResponse = (response) => {
    setApikeyHeader({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${response.apiKey}`,
    });
    setApiKey(response.apiKey);
    setLogs(response.logs);
    setBotVersion(response.bot_version);
    setBotVersionLastUsed(response.bot_version_last_used);
  };
  
  
  useEffect(() => {
    setLoading(true);
    dispatch(getFeedbackAPIkey())
      .then(
        (response) => {
          handleResponse(response);
          setLoading(false);
        }
      );
  }, []);
    
  

  if (loading) {
    return (<div><ProgessBar /></div>);
  }
  if (!apiKey) {
    return null;
  }

  const onCopy = () => {
    setCopied(true);
    delay(() => {
      setCopied(false);
    }, 2000);
  }

  const openModal = () => {
    props.initialize({});
    setShowLogs(false);
    setSubmitted(false);
    setShowModal(true);
  }

  const closeModal = () => {
    setShowModal(false);
  }

  const toggleShowLogs = () => {
    setShowLogs(!showLogs);
  }

  const submit = (params) => {
    return dispatch(refreshFeedbackAPIkey(params))
      .then(
        (response) => {
          handleResponse(response);
          setSubmitted(true);
        },
        (error) => {
          const e = error.data || error;
          if (e.message) {
            e._error = e.message;
          }
          throw new SubmissionError(e);
        },
      );
  }

  const renderField = ({
    input, label, componentClass, placeholder, type, meta: { touched, error }, className,
  }) => {
    const validationState = touched && error ? 'error' : null;
    return (
      <FormGroup className={className} validationState={validationState}>
        <ControlLabel>{label}</ControlLabel>
        <FormControl
          {...input}
          componentClass={componentClass}
          placeholder={placeholder}
          type={type}
          disabled={submitting}
        />
        {error && touched && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }


  return (
    <div>
      <hr />
      <FormGroup>
        <ControlLabel>Access Token</ControlLabel>
        <HelpBlock>Add your access token to the TruVoice Automation Discovery Bots custom settings to enable communication between TruVoice Automation and the <CRMInstanceName /> TruVoice Automation Discovery Bots application.</HelpBlock>
        <div >
          <JSONPretty style={{ margin: 0 }} data={apikeyHeader} theme={JSONPrettyMon} />
        </div>

        <div className="clearfix">
          <div className="pull-left">
            {!copied &&
              <CopyToClipboard
                text={`Bearer ${apiKey}`}
                onCopy={onCopy}
              >
                <a
                  href=""
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <Fa name="copy" /> Copy Access Token
                </a>
              </CopyToClipboard>
            }
            {copied && <span className="text-success">Authentication Code Copied to clipboard!</span>}
          </div>
          <div className="pull-right">
            <Button onClick={openModal} bsSize="sm" bsStyle="default"><Fa name="refresh" /> Refresh API Key</Button>
          </div>
        </div>
      </FormGroup>
      {botVersion !== null &&
        <div>
          <hr />
          <FormGroup>
            <em className="text-muted">
              Last request was made from app version: <strong>{botVersion}</strong>
              {botVersionLastUsed !== null && <span>&nbsp;on <DatetimeDisplay value={botVersionLastUsed} format="longWithTime" /></span>}
            </em>
          </FormGroup>
        </div>
      }

      <Modal show={showModal} onHide={closeModal}>
          <form onSubmit={props.handleSubmit(submit)}>
            <Modal.Header closeButton>
              <h4>Refresh API Key</h4>
            </Modal.Header>
            <Modal.Body>
              {!submitted &&
                <div>
                  <p>Please fill out this form to reset your organization's API key:</p> 
                  <Field name="reason" component={renderField} componentClass="textarea" placeholder="Please provide a brief reason why you need to refresh the API key. This will be logged for reference purpose."/>
                </div>
              }
              {submitted &&
                <div className="alert alert-success animated flash">API key has been refreshed!</div>
              }
              <p>&nbsp;</p>
              <div className="clearfix">
                <div className="pull-left">
                  <Button bsStyle="link" onClick={toggleShowLogs}>{showLogs ? 'Hide' : 'Show'} Change Logs{showLogs ? '' : ` (${logs.length})`}</Button>
                </div>
                <div className="pull-right">
                  {!submitted &&
                    <Button bsStyle="primary" type="submit" disabled={submitting || invalid}>
                      {!submitting && <span><Fa name="refresh" /> Refresh API Key</span>}
                      {submitting && <span><Fa name="spinner" spin /> Refreshing API Key</span>}
                    </Button>
                  }
                  {submitted && <Button bsStyle="default" onClick={closeModal}>Done</Button>}
                </div>
              </div>

              {logs.length > 0 && showLogs &&
                <div>
                  <p />
                  <hr />
                  <p />
                  <div><strong>API Key Change Logs:</strong></div>
                  <table className="table table-condensed table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => {
                        const user = log.user?.first_name ? `${log.user.first_name} ${log.user.last_name}` : log.user__id;

                        return (
                          <tr key={log._id}>
                            <td><DatetimeDisplay value={log.created_at} format="shortWithTime" /></td>
                            <td>{user}</td>
                            <td>{log.value}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              }
            </Modal.Body>
          </form>
      </Modal>
    </div>  
  );
}


APIKeyInfo.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
};
APIKeyInfo.defaultProps = {

};

const validate = (values) => {
  const errors = {};
  if (!values.reason) {
    errors.reason = 'A brief reason is required.';
  }
  return errors;
};

export default reduxForm({
  form: 'apiKeyForm',
  validate,
})(APIKeyInfo);